import { useEffect, useRef } from "react";
import {
  calloutMigrations,
  calloutProps,
  DEFAULT_CALLOUT_WIDTH,
  isKey,
  type CalloutShape,
  type CalloutTag,
  type CalloutTerminator,
} from "sketch/shapes/callout";
import {
  Edge2d,
  Group2d,
  Rectangle2d,
  SVGContainer,
  getColorValue,
  ShapeUtil,
  Vec,
  type Editor,
  type Geometry2d,
  type SvgExportContext,
  type IndexKey,
  type TLHandle,
  type TLHandleDragInfo,
  type TLIndicatorPath,
  type TLResizeInfo,
} from "tldraw";
import { invariant } from "ts-invariant";

import {
  calloutBindingOf,
  calloutLayout,
  letteringMask,
  MAX_CALLOUT_WIDTH,
  MIN_CALLOUT_WIDTH,
  tagPoints,
  widthHandlePoint,
  type CalloutLayout,
} from "@/sketch/callout-geometry";
import {
  SHEET_FONT,
  SHEET_FONT_EXPORT,
  SHEET_FONT_FACE,
  SHEET_LEADING,
  SHEET_SIZE,
  SHEET_TRACKING,
} from "@/sketch/lettering";

/**
 * A callout: an architectural leader note. The lettering (or keynote tag)
 * sits at the head, the leader runs from it to whatever the note is about,
 * and the terminator says what it lands on. The leader's far end is either
 * a `callout` binding (CalloutBindingUtil writes the resolved point back, so
 * the leader follows the element) or the free point in props; dragging the
 * shape moves the head and leaves a bound leader where it points.
 */
interface CalloutInk {
  stroke: string;
  /** What the lettering's mask and the tag bubble are filled with. */
  mask: string;
  font: string;
}

const CANVAS_INK: CalloutInk = {
  stroke: "var(--tl-color-text-1)",
  mask: "var(--tl-color-background)",
  font: SHEET_FONT,
};

/** Exports have no tldraw CSS variables, so they name colors and the
 *  embedded font family outright. */
const EXPORT_INK: CalloutInk = {
  stroke: "#1d1d1d",
  mask: "#fff",
  font: SHEET_FONT_EXPORT,
};

function calloutSvg(
  layout: CalloutLayout,
  ink: CalloutInk,
  tag: CalloutTag,
  terminator: CalloutTerminator,
) {
  const { k, leader, aim, lines, head, radius } = layout;
  const color = ink.stroke;
  const mask = letteringMask(layout);
  const path = leader
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  const terminatorGlyph = () => {
    const tip = leader[0]!;
    if (terminator === "none") return null;
    if (terminator === "dot") {
      return <circle cx={tip.x} cy={tip.y} r={3 * k} fill={color} />;
    }
    const back = Vec.Sub(tip, aim.clone().mul(10 * k));
    const side = new Vec(-aim.y, aim.x).mul(3 * k);
    return (
      <path
        d={`M ${tip.x} ${tip.y} L ${back.x + side.x} ${back.y + side.y} L ${back.x - side.x} ${back.y - side.y} Z`}
        fill={color}
      />
    );
  };

  return (
    <g>
      <path d={path} stroke={color} strokeWidth={1 * k} fill="none" />
      {terminatorGlyph()}
      {tag === "circle" && (
        <circle
          cx={head.x}
          cy={head.y}
          r={radius}
          stroke={color}
          strokeWidth={1 * k}
          fill={ink.mask}
        />
      )}
      {tag === "hex" && (
        <polygon
          points={tagPoints(head, radius)
            .map((p) => `${p.x},${p.y}`)
            .join(" ")}
          stroke={color}
          strokeWidth={1 * k}
          fill={ink.mask}
        />
      )}
      {tag === "none" && (
        <rect
          x={mask.x}
          y={mask.y}
          width={mask.w}
          height={mask.h}
          fill={ink.mask}
        />
      )}
      <text
        fontSize={SHEET_SIZE * k}
        fontFamily={ink.font}
        letterSpacing={SHEET_TRACKING * SHEET_SIZE * k}
        fill={color}
      >
        {lines.map((line, i) => (
          <tspan key={i} x={line.x} y={line.baseline}>
            {line.text}
          </tspan>
        ))}
      </text>
    </g>
  );
}

export class CalloutShapeUtil extends ShapeUtil<CalloutShape> {
  static override type = "callout" as const;
  static override props = calloutProps;
  // The app store derives its schema from the utils, so the migrations must
  // ride here too, or the two sides disagree about the callout sequence.
  static override migrations = calloutMigrations;

  /** The shape's ink in the current theme: what its leader and lettering draw in. */
  private inkOf(shape: CalloutShape, mode: "dark" | "light") {
    return getColorValue(
      this.editor.getCurrentTheme().colors[mode],
      shape.props.color,
      "solid",
    );
  }

  override getDefaultProps(): CalloutShape["props"] {
    return {
      text: "",
      color: "black",
      tag: "none",
      terminator: "arrow",
      width: DEFAULT_CALLOUT_WIDTH,
      // The sheet places a head nobody authored.
      head: null,
      target: { x: 0, y: 0 },
    };
  }

  override canResize() {
    return false;
  }

  /** Annotation, not geometry: never a bounds-snap target. */
  override getBoundsSnapGeometry() {
    return { points: [] };
  }

  /** The indicator already draws the leader and the block, which is what a
   *  selected callout is. A bounding box around both spans the drawing and
   *  says nothing: annotation shows itself, not a rectangle. */
  override hideSelectionBoundsFg() {
    return true;
  }

  override hideResizeHandles() {
    return true;
  }

  override hideRotateHandle() {
    return true;
  }

  /** Double-click the lettering to type the note. */
  override canEdit() {
    return true;
  }

  /** What this shape says, for everything that reads a drawing as text:
   *  the agent context builder, search, exports. */
  override getText(shape: CalloutShape) {
    return shape.props.text;
  }

  /** The sheet face, so tldraw loads it (which re-measures the lettering
   *  once it lands) and embeds it in exports. */
  override getFontFaces() {
    return [SHEET_FONT_FACE];
  }

  /** A callout with nothing to say is a stray leader. Editing ending is
   *  what decides that, not the input unmounting: the input comes and goes
   *  with a re-render, editing ends once. */
  override onEditEnd(shape: CalloutShape) {
    const current = this.editor.getShape<CalloutShape>(shape.id);
    if (current && current.props.text.trim() === "") {
      this.editor.deleteShapes([shape.id]);
    }
  }

  override onResize(shape: CalloutShape, _info: TLResizeInfo<CalloutShape>) {
    return shape;
  }

  /** The editor's half of the tag contract: a tag holds a key. A write
   *  that would put a note in a bubble (typing past the key, or tagging a
   *  note from the style panel) keeps the head and text it had. */
  override onBeforeUpdate(prev: CalloutShape, next: CalloutShape) {
    if (next.props.tag === "none" || isKey(next.props.text)) return;
    return {
      ...next,
      props: { ...next.props, tag: prev.props.tag, text: prev.props.text },
    };
  }

  /** A drag authors the head the sheet had been placing, before the drag
   *  moves anything, so the end of the drag has a real head to offset. */
  override onTranslateStart(shape: CalloutShape) {
    if (shape.props.head) return;
    const { head } = calloutLayout(this.editor, shape);
    return {
      id: shape.id,
      type: "callout" as const,
      props: { head: { x: head.x, y: head.y } },
    };
  }

  override getGeometry(shape: CalloutShape): Geometry2d {
    const { leader, box } = calloutLayout(this.editor, shape);
    const segments = leader
      .slice(1)
      .map((point, i) => new Edge2d({ start: leader[i]!, end: point }));
    return new Group2d({
      children: [
        ...segments,
        new Rectangle2d({
          x: box.x,
          y: box.y,
          width: Math.max(box.w, 1),
          height: Math.max(box.h, 1),
          isFilled: true,
        }),
      ],
    });
  }

  override component(shape: CalloutShape) {
    const layout = calloutLayout(this.editor, shape);
    const isEditing = this.editor.getEditingShapeId() === shape.id;
    return (
      <SVGContainer>
        {/* One renderer at a time: while editing, the input holds the
            lettering, so the drawing gives up its lines rather than
            doubling them behind a transparent box. */}
        {calloutSvg(
          isEditing ? { ...layout, lines: [] } : layout,
          {
            ...CANVAS_INK,
            stroke: this.inkOf(shape, this.editor.getColorMode()),
          },
          shape.props.tag,
          shape.props.terminator,
        )}
        {isEditing && (
          <foreignObject
            x={layout.box.x}
            y={layout.box.y}
            width={layout.box.w}
            height={layout.box.h}
          >
            <CalloutTextInput
              editor={this.editor}
              shape={shape}
              layout={layout}
            />
          </foreignObject>
        )}
      </SVGContainer>
    );
  }

  /** One handle, on the edge the lettering wraps at: a note is a block with
   *  a natural width, and this is how the drafter changes it. */
  override getHandles(shape: CalloutShape): TLHandle[] {
    if (shape.props.tag !== "none") return [];
    const point = widthHandlePoint(calloutLayout(this.editor, shape));
    return [
      {
        id: "width",
        type: "vertex",
        index: "a1" as IndexKey,
        x: point.x,
        y: point.y,
      },
    ];
  }

  override onHandleDrag(
    shape: CalloutShape,
    { handle }: TLHandleDragInfo<CalloutShape>,
  ) {
    const layout = calloutLayout(this.editor, shape);
    const dragged = ((handle.x - layout.head.x) * layout.side) / layout.k;
    return {
      ...shape,
      props: {
        ...shape.props,
        width: clamp(dragged, MIN_CALLOUT_WIDTH, MAX_CALLOUT_WIDTH),
      },
    };
  }

  override toSvg(shape: CalloutShape, ctx: SvgExportContext) {
    return calloutSvg(
      calloutLayout(this.editor, shape),
      { ...EXPORT_INK, stroke: this.inkOf(shape, ctx.colorMode) },
      shape.props.tag,
      shape.props.terminator,
    );
  }

  override getIndicatorPath(shape: CalloutShape): TLIndicatorPath {
    const { leader, box } = calloutLayout(this.editor, shape);
    const path = new Path2D();
    leader.forEach((point, i) => {
      if (i === 0) path.moveTo(point.x, point.y);
      else path.lineTo(point.x, point.y);
    });
    path.rect(box.x, box.y, box.w, box.h);
    return path;
  }

  /** Dragging a callout moves its head; a bound leader keeps pointing at
   *  what it annotates, which is the whole point of binding it. The head
   *  is read off the live record: translation moves x/y, never props, and
   *  onTranslateStart authored one if the sheet had been placing it. */
  override onTranslateEnd(initial: CalloutShape, current: CalloutShape) {
    const delta = new Vec(current.x - initial.x, current.y - initial.y);
    if (delta.len() === 0) return;
    const { target } = initial.props;
    const { head } = current.props;
    invariant(head, "a dragged callout has a head");
    const bound = calloutBindingOf(this.editor, current.id) !== undefined;
    this.editor.updateShape<CalloutShape>({
      id: current.id,
      type: "callout",
      x: 0,
      y: 0,
      props: {
        head: { x: head.x + delta.x, y: head.y + delta.y },
        target: bound
          ? target
          : { x: target.x + delta.x, y: target.y + delta.y },
      },
    });
  }
}

/**
 * Typing a note. The input is the note's own block: same width, same
 * lettering, same place, no chrome of its own — the shape's selection
 * outline is the only box on screen, and text wraps where it will wrap once
 * you're done. tldraw draws the caret and the selection; we draw nothing.
 */
function CalloutTextInput({
  editor,
  shape,
  layout,
}: {
  editor: Editor;
  shape: CalloutShape;
  layout: CalloutLayout;
}) {
  const scale = layout.k;
  // The shape is the only copy of the note: typing writes it straight
  // through, so what you see on the sheet is what is stored, and peers watch
  // it land. Escape puts back the text the edit started with.
  const original = useRef(shape.props.text);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const write = (text: string) =>
    editor.updateShape<CalloutShape>({
      id: shape.id,
      type: "callout",
      props: { text },
    });

  // `autoFocus` fires before tldraw refocuses its container on edit-start,
  // so the input claims focus on the next frame (and once more after the
  // container settles), the same dance the dim's value input does.
  useEffect(() => {
    const focus = () => {
      const el = inputRef.current;
      if (el && document.activeElement !== el) {
        el.focus();
        el.select();
      }
    };
    const raf = requestAnimationFrame(focus);
    const timer = setTimeout(focus, 80);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, []);

  return (
    <textarea
      ref={inputRef}
      value={shape.props.text}
      onChange={(e) => write(e.target.value)}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        // Shift-Enter is the second line of a note; Enter finishes it.
        if (e.key === "Enter" && e.shiftKey) return;
        if (e.key === "Enter") editor.setEditingShape(null);
        if (e.key === "Escape") {
          write(original.current);
          editor.setEditingShape(null);
        }
      }}
      style={{
        width: "100%",
        height: "100%",
        display: "block",
        resize: "none",
        overflow: "hidden",
        textAlign:
          shape.props.tag !== "none"
            ? "center"
            : layout.side === 1
              ? "left"
              : "right",
        // CSS px inside a foreignObject are page units; scale keeps the
        // input's lettering screen-constant like the rest of the shape.
        fontSize: SHEET_SIZE * scale,
        lineHeight: `${SHEET_LEADING * scale}px`,
        letterSpacing: SHEET_TRACKING * SHEET_SIZE * scale,
        fontFamily: SHEET_FONT,
        // The sheet sets caps, so typing shows caps; the note keeps the
        // case it was typed in, which is what the agent reads back.
        textTransform: "uppercase",
        border: "none",
        padding: 0,
        margin: 0,
        // The input holds the lettering while editing, so it holds the
        // background mask too: the grid must not read through what you type.
        background: "var(--tl-color-background)",
        color: "var(--tl-color-text-1)",
        outline: "none",
      }}
    />
  );
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}
